import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '../../services/api';

export interface ChatMessage {
  id: string;
  _id?: string;
  projectId: string;
  userId?: string | {
    profile?: {
      displayName?: string;
      firstName?: string;
      lastName?: string;
      avatar?: string;
    };
    email?: string;
  };
  parentId?: string | {
    content: string;
    userId?: string | {
      profile?: {
        displayName?: string;
        firstName?: string;
        lastName?: string;
      };
    };
  };
  content: string;
  edited: boolean;
  editHistory?: Array<{ content: string; editedAt: string }>;
  reactions?: Array<{ userId: string; type: string; createdAt: string }>;
  reactionSummary?: Record<string, number>;
  userReactions?: Record<string, boolean>;
  createdAt: string;
  updatedAt: string;
}

interface ChatState {
  byProject: Record<string, {
    messages: ChatMessage[];
    isLoading: boolean;
    error: string | null;
    hasMore: boolean;
  }>;
  sending: Record<string, boolean>;
  updating: Record<string, boolean>;
  deleting: Record<string, boolean>;
}

const initialState: ChatState = {
  byProject: {},
  sending: {},
  updating: {},
  deleting: {},
};

export const fetchChatMessages = createAsyncThunk(
  'chat/fetchMessages',
  async (
    { projectId, limit = 50, before }: { projectId: string; limit?: number; before?: string },
    { getState }
  ) => {
    const state = getState() as { auth: { accessToken: string | null } };
    const token = state.auth.accessToken || undefined;
    const messages = await api.chat.getMessages(projectId, { limit, before }, token);
    return { projectId, messages, hasMore: messages.length === limit };
  }
);

export const sendChatMessage = createAsyncThunk(
  'chat/sendMessage',
  async (
    { projectId, content, parentId }: { projectId: string; content: string; parentId?: string },
    { getState }
  ) => {
    const state = getState() as { auth: { accessToken: string | null } };
    if (!state.auth.accessToken) {
      throw new Error('Authentication required');
    }
    const message = await api.chat.sendMessage(projectId, { content, parentId }, state.auth.accessToken);
    return { projectId, message };
  }
);

export const editChatMessage = createAsyncThunk(
  'chat/editMessage',
  async ({ messageId, content }: { messageId: string; content: string }, { getState }) => {
    const state = getState() as { auth: { accessToken: string | null } };
    if (!state.auth.accessToken) {
      throw new Error('Authentication required');
    }
    const message = await api.chat.editMessage(messageId, content, state.auth.accessToken);
    return { message };
  }
);

export const deleteChatMessage = createAsyncThunk(
  'chat/deleteMessage',
  async (messageId: string, { getState }) => {
    const state = getState() as { auth: { accessToken: string | null } };
    if (!state.auth.accessToken) {
      throw new Error('Authentication required');
    }
    await api.chat.deleteMessage(messageId, state.auth.accessToken);
    return { messageId };
  }
);

export const addChatReaction = createAsyncThunk(
  'chat/addReaction',
  async ({ messageId, type }: { messageId: string; type: string }, { getState }) => {
    const state = getState() as { auth: { accessToken: string | null } };
    if (!state.auth.accessToken) {
      throw new Error('Authentication required');
    }
    const data = await api.chat.addReaction(messageId, type, state.auth.accessToken);
    return { messageId, ...data };
  }
);

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    addMessageFromWS: (state, action: PayloadAction<{ projectId: string; message: ChatMessage }>) => {
      if (!state.byProject[action.payload.projectId]) {
        state.byProject[action.payload.projectId] = {
          messages: [],
          isLoading: false,
          error: null,
          hasMore: false,
        };
      }
      state.byProject[action.payload.projectId].messages.push(action.payload.message);
    },
    updateMessageFromWS: (state, action: PayloadAction<{ projectId: string; message: ChatMessage }>) => {
      if (state.byProject[action.payload.projectId]) {
        const index = state.byProject[action.payload.projectId].messages.findIndex(
          (m) => m.id === action.payload.message.id || m._id === action.payload.message.id
        );
        if (index !== -1) {
          state.byProject[action.payload.projectId].messages[index] = action.payload.message;
        }
      }
    },
    deleteMessageFromWS: (state, action: PayloadAction<{ projectId: string; messageId: string }>) => {
      if (state.byProject[action.payload.projectId]) {
        state.byProject[action.payload.projectId].messages = state.byProject[action.payload.projectId].messages.filter(
          (m) => m.id !== action.payload.messageId && m._id !== action.payload.messageId
        );
      }
    },
    updateMessageReactions: (state, action: PayloadAction<{ projectId: string; messageId: string; reactionSummary: Record<string, number>; userReactions: Record<string, boolean> }>) => {
      if (state.byProject[action.payload.projectId]) {
        const index = state.byProject[action.payload.projectId].messages.findIndex(
          (m) => m.id === action.payload.messageId || m._id === action.payload.messageId
        );
        if (index !== -1) {
          state.byProject[action.payload.projectId].messages[index].reactionSummary = action.payload.reactionSummary;
          state.byProject[action.payload.projectId].messages[index].userReactions = action.payload.userReactions;
        }
      }
    },
    setSending: (state, action: PayloadAction<{ projectId: string; sending: boolean }>) => {
      state.sending[action.payload.projectId] = action.payload.sending;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchChatMessages.pending, (state, action) => {
        if (!state.byProject[action.meta.arg.projectId]) {
          state.byProject[action.meta.arg.projectId] = {
            messages: [],
            isLoading: true,
            error: null,
            hasMore: false,
          };
        } else {
          state.byProject[action.meta.arg.projectId].isLoading = true;
        }
      })
      .addCase(fetchChatMessages.fulfilled, (state, action) => {
        if (!state.byProject[action.payload.projectId]) {
          state.byProject[action.payload.projectId] = {
            messages: [],
            isLoading: false,
            error: null,
            hasMore: false,
          };
        }
        // If before parameter was used, prepend messages (for pagination)
        if (action.meta.arg.before) {
          state.byProject[action.payload.projectId].messages = [
            ...action.payload.messages,
            ...state.byProject[action.payload.projectId].messages,
          ];
        } else {
          state.byProject[action.payload.projectId].messages = action.payload.messages;
        }
        state.byProject[action.payload.projectId].isLoading = false;
        state.byProject[action.payload.projectId].hasMore = action.payload.hasMore;
        state.byProject[action.payload.projectId].error = null;
      })
      .addCase(fetchChatMessages.rejected, (state, action) => {
        if (state.byProject[action.meta.arg.projectId]) {
          state.byProject[action.meta.arg.projectId].isLoading = false;
          state.byProject[action.meta.arg.projectId].error = action.error.message || 'Failed to fetch messages';
        }
      })
      .addCase(sendChatMessage.pending, (state, action) => {
        state.sending[action.meta.arg.projectId] = true;
      })
      .addCase(sendChatMessage.fulfilled, (state, action) => {
        state.sending[action.payload.projectId] = false;
        if (!state.byProject[action.payload.projectId]) {
          state.byProject[action.payload.projectId] = {
            messages: [],
            isLoading: false,
            error: null,
            hasMore: false,
          };
        }
        state.byProject[action.payload.projectId].messages.push(action.payload.message);
      })
      .addCase(sendChatMessage.rejected, (state, action) => {
        state.sending[action.meta.arg.projectId] = false;
      })
      .addCase(editChatMessage.pending, (state, action) => {
        state.updating[action.meta.arg.messageId] = true;
      })
      .addCase(editChatMessage.fulfilled, (state, action) => {
        state.updating[action.meta.arg.messageId] = false;
        // Update message in all projects
        Object.keys(state.byProject).forEach((projectId) => {
          const project = state.byProject[projectId];
          const index = project.messages.findIndex(
            (m) => m.id === action.payload.message.id || m._id === action.payload.message.id
          );
          if (index !== -1) {
            project.messages[index] = action.payload.message;
          }
        });
      })
      .addCase(editChatMessage.rejected, (state, action) => {
        state.updating[action.meta.arg.messageId] = false;
      })
      .addCase(deleteChatMessage.pending, (state, action) => {
        state.deleting[action.meta.arg] = true;
      })
      .addCase(deleteChatMessage.fulfilled, (state, action) => {
        state.deleting[action.payload.messageId] = false;
        // Remove message from all projects
        Object.keys(state.byProject).forEach((projectId) => {
          state.byProject[projectId].messages = state.byProject[projectId].messages.filter(
            (m) => m.id !== action.payload.messageId && m._id !== action.payload.messageId
          );
        });
      })
      .addCase(deleteChatMessage.rejected, (state, action) => {
        state.deleting[action.meta.arg] = false;
      })
      .addCase(addChatReaction.fulfilled, (state, action) => {
        // Update message reactions in all projects
        Object.keys(state.byProject).forEach((projectId) => {
          const project = state.byProject[projectId];
          const index = project.messages.findIndex(
            (m) => m.id === action.payload.messageId || m._id === action.payload.messageId
          );
          if (index !== -1) {
            project.messages[index].reactionSummary = action.payload.reactionSummary;
            project.messages[index].userReactions = { [action.payload.userReaction || '']: !!action.payload.userReaction };
          }
        });
      });
  },
});

export const { addMessageFromWS, updateMessageFromWS, deleteMessageFromWS, updateMessageReactions, setSending } = chatSlice.actions;
export default chatSlice.reducer;
